import { useRef } from "react";
import { useState } from "react";
import { useEffect } from "react";
import './youtube-player-control.scss';
import { IconMediaControlFullscreen, IconMediaControlPause, IconMediaControlPlay, IconMediaControlSoundOff, IconMediaControlSoundOn } from "../../icons/svgs";

function VideoPreviewYoutube({url}){
    const playerRef = useRef(null);
    // playerRef.current terisi SINKRON begitu `new window.YT.Player(...)` dipanggil,
    // tapi method seperti playVideo()/pauseVideo() baru benar-benar bisa dipanggil
    // setelah event onReady - klik kontrol di jendela waktu antara keduanya
    // melempar "X is not a function" walau playerRef.current sudah truthy (TB-20826).
    const isPlayerReadyRef = useRef(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(100);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const extractVideoID = ( url ) => {
        const regExp = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})(?:\S+)?$/;
        const match = url.match(regExp);
        if (match && match[1]) {
            return match[1];
        }
        return false;
    }

    let validYoutubeUrl = false;
    let videoId = "";
    if (url) {
        videoId = extractVideoID(url);
        if ( videoId ) {
            validYoutubeUrl = true;
        }
    }

    const loadYouTubeAPI = () => {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    };

    const initializePlayer = () => {
        playerRef.current = new window.YT.Player('player', {
          videoId,
          playerVars: {
            controls: 0,
            rel: 0,
            modestbranding: 1,
            showinfo: 0
          },
          events: {
            onReady: handlePlayerReady,
            onStateChange: handlePlayerStateChange
          }
        });
    };

    const handlePlayerReady = () => {
      isPlayerReadyRef.current = true;
      if (playerRef.current && playerRef.current.getDuration) {
        const duration = playerRef.current.getDuration();
        setDuration(duration);
      } else {
        console.error("getDuration is not available yet.");
      }
    };

    const handlePlayerStateChange = (event) => {
        if (event.data === window.YT.PlayerState.PLAYING) {
          setIsPlaying(true);
          const updateTime = () => {
            if (!playerRef.current) {
                return;
            }
            setCurrentTime(playerRef.current.getCurrentTime());
            if (playerRef.current.getPlayerState() === window.YT.PlayerState.PLAYING) {
                requestAnimationFrame(updateTime);
            }
          };
          requestAnimationFrame(updateTime);
        } else {
          setIsPlaying(false);
        }
    };

    // UseEffect to load YouTube API on component mount
    useEffect(() => {
        if (!window.YT) {
          loadYouTubeAPI();
          window.onYouTubeIframeAPIReady = initializePlayer;
        } else {
          initializePlayer();
        }

        return () => {
          // Cleanup the player on unmount
          isPlayerReadyRef.current = false;
          if (playerRef.current) {
            playerRef.current.destroy();
            playerRef.current = null;
          }
        };
    }, []);

    const playPauseVideo = () => {
        if (!playerRef.current || !isPlayerReadyRef.current) {
            return;
        }
        if (isPlaying) {
          playerRef.current.pauseVideo();
        } else {
            playerRef.current.playVideo();
        }
    };

    const muteUnmuteVideo = () => {
        if (!playerRef.current || !isPlayerReadyRef.current) {
            return;
        }
        if (isMuted) {
          playerRef.current.unMute();
          setIsMuted(false);
        } else {
          playerRef.current.mute();
          setIsMuted(true);
        }
    };

    const changeVolume = (event) => {
        const newVolume = event.target.value;
        setVolume(newVolume);
        if (playerRef.current && isPlayerReadyRef.current) {
            playerRef.current.setVolume(newVolume);
        }
    };

    const seekTo = (event) => {
        const seekTime = (event.target.value / 100) * duration;
        if (playerRef.current && isPlayerReadyRef.current) {
            playerRef.current.seekTo(seekTime, true);
        }
    };

    const toggleFullScreen = () => {
        const iframe = document.getElementById('player');
        if (iframe.requestFullscreen) {
          iframe.requestFullscreen();
        } else if (iframe.mozRequestFullScreen) {
          iframe.mozRequestFullScreen(); // Firefox
        } else if (iframe.webkitRequestFullscreen) {
          iframe.webkitRequestFullscreen(); // Chrome, Safari, Opera
        } else if (iframe.msRequestFullscreen) {
          iframe.msRequestFullscreen(); // IE/Edge
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
    };

    return (
        <div style={{'width':'100%'}}>
            <div style={{ position: 'relative', width: '100%', height: '315px' }}>
                <div id="player" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}></div>

                <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    pointerEvents: 'auto'
                }}
                ></div>
            </div>

            <div className="controls">
                <button className="button" onClick={playPauseVideo}>
                {isPlaying ? <IconMediaControlPause /> : <IconMediaControlPlay />}
                </button>

                <button className="button" onClick={muteUnmuteVideo}>
                {isMuted ? <IconMediaControlSoundOff /> : <IconMediaControlSoundOn />}
                </button>

                <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={changeVolume}
                className="slider"
                />

                <span className="time">
                {formatTime(currentTime)} / {formatTime(duration)}
                </span>

                <input
                type="range"
                min="0"
                max="100"
                value={(currentTime / duration) * 100 || 0}
                onChange={seekTo}
                className="seek-bar"
                />

                <button className="button" onClick={toggleFullScreen}><IconMediaControlFullscreen/></button>
            </div>
        </div>
    );
}

export default VideoPreviewYoutube;
